import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface Player {
  id: string;
  name: string;
}

interface Game {
  buyin_amount: number;
  chips_per_buyin: number;
}

interface AddBuyinDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  gameId: string;
  players: Player[];
  game: Game;
  onBuyinAdded: () => void;
}

export const AddBuyinDialog = ({ open, onOpenChange, gameId, players, game, onBuyinAdded }: AddBuyinDialogProps) => {
  const [selectedPlayerId, setSelectedPlayerId] = useState("");
  const [amount, setAmount] = useState(game.buyin_amount.toString());
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const chips = Math.round((parseFloat(amount) || 0) / game.buyin_amount * game.chips_per_buyin);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPlayerId || !amount) return;

    setLoading(true);
    try {
      const { error } = await supabase
        .from('buyins')
        .insert({ 
          player_id: selectedPlayerId,
          game_id: gameId,
          amount: parseFloat(amount),
          chips: chips
        });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Buy-in recorded successfully"
      });

      setSelectedPlayerId("");
      setAmount(game.buyin_amount.toString());
      onOpenChange(false);
      onBuyinAdded();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to record buy-in",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Buy-in</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="player">Player</Label>
            <Select value={selectedPlayerId} onValueChange={setSelectedPlayerId}>
              <SelectTrigger>
                <SelectValue placeholder="Select a player" />
              </SelectTrigger>
              <SelectContent>
                {players.map((player) => (
                  <SelectItem key={player.id} value={player.id}>
                    {player.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="amount">Amount (₹)</Label>
            <Input
              id="amount"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Enter buy-in amount"
              step="0.01"
              min="0"
            />
          </div>
          
          <div className="bg-muted/50 p-4 rounded space-y-2">
            <div className="flex justify-between">
              <span>Standard Amount:</span>
              <span className="font-medium">₹{game.buyin_amount}</span>
            </div>
            <div className="flex justify-between">
              <span>Chips (calculated):</span>
              <span className="font-medium">{chips.toLocaleString()}</span>
            </div>
          </div>

          <div className="flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading || !selectedPlayerId || !amount}>
              {loading ? "Recording..." : "Record Buy-in"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};